
import { defineComponent, Ref, ref, computed, onMounted, onUnmounted } from "vue";
import Api from "@/services/Api";
import useEmitter from "@/composables/Emmiter";
import EditaVeiculo from "./components/EditaVeiculo.vue";
import AdicionarVeiculo from "./components/AdicionarVeiculo.vue";
import Title from "@/components/Title.vue"
import Button from "@/components/Button.vue"
import { Modal } from "bootstrap";

interface VehicleFilterType {
  marca: string;
  modelo: string;
  fipe: string;
  anoInicial: string;
  anoFinal: string;
  comFiltro: boolean | null;
}

const filtroVazio = (): VehicleFilterType => ({ marca: "", modelo: "", fipe: "", anoInicial: "", anoFinal: "", comFiltro: null });

const filtrosVeiculo = [
  { label: "Filtro Oleo", chave: "filtro_oleo" },
  { label: "Filtro Ar", chave: "filtro_ar" },
  { label: "Filtro Ac", chave: "filtro_ac" },
  { label: "Filtro Comb", chave: "filtro_combustivel" },
  { label: "Total", chave: "filtros", destacaSempre: true },
];

export default defineComponent({
  name: "Vehicle",

  components: {
    EditaVeiculo,
    Title,
    Button,
    AdicionarVeiculo
  },

  setup() {
    const emitter = useEmitter();

    const maxLimitByPage = 25;

    const resultados: Ref<number> = ref(0);

    const vehicleLoad: Ref<boolean> = ref(false);

    // contador (e não boolean) para duas buscas simultâneas não desligarem o loading uma da outra
    const buscasEmAndamento: Ref<number> = ref(0);
    const buscandoVeiculos = computed(() => buscasEmAndamento.value > 0);

    const primeiraBuscaFeita: Ref<boolean> = ref(false);

    const editarMultiplos: Ref<boolean> = ref(false);

    const veiculosSelecionado: Ref<any[]> = ref([]);

    const vehiclesArray: Ref<any[]> = ref([]);

    const page: Ref<number | null> = ref(null);

    const vehicleFilter: Ref<VehicleFilterType> = ref(filtroVazio());

    const fastFind: Ref<string> = ref('');

    let observer: IntersectionObserver | null = null;

    // identifica a última busca disparada, para descartar respostas fora de ordem
    let ultimaRequisicao = 0;

    const handlePage = async (paramPage: number) => {
      if (vehicleLoad.value) return;

      await searchVehicle(paramPage, maxLimitByPage);
    };

    /** Retorna os veículos carregados, ou null se a busca foi substituída por outra mais recente. */
    async function searchVehicle(pageParam: number, limit: number, search = false): Promise<any[] | null> {
      const requisicao = ++ultimaRequisicao;

      vehicleLoad.value = true;

      const { data } = await Api.get("getVehiclesByPage", { page: pageParam, limit, ...vehicleFilter.value });

      if (requisicao !== ultimaRequisicao) return null;

      page.value = pageParam;

      vehiclesArray.value = search ? data.veiculos : [...vehiclesArray.value, ...data.veiculos];

      primeiraBuscaFeita.value = true;
      vehicleLoad.value = false;

      return vehiclesArray.value;
    }

    /** Mantém o loading da tela ligado enquanto a busca (input ou modal de filtro) estiver rodando. */
    async function comLoading<T>(busca: () => Promise<T>): Promise<T> {
      buscasEmAndamento.value++;

      try {
        return await busca();
      } finally {
        buscasEmAndamento.value--;
      }
    }

    async function buscaTotalVeiculos() {
      const { data } = await Api.get("buscaTotalVeiculos", { ...vehicleFilter.value });

      resultados.value = data.resultados;
    }

    /** Recarrega a lista da primeira página usando o filtro atual. */
    async function aplicaFiltro() {
      // as duas em paralelo, para o loading só sair quando a lista e o total estiverem prontos.
      // uma falha no total não pode derrubar a busca da lista.
      const [veiculos] = await Promise.all([
        searchVehicle(1, maxLimitByPage, true),
        buscaTotalVeiculos().catch(() => undefined),
      ]);

      return veiculos;
    }

    // busca manual: só roda no enter, no botão Buscar ou ao limpar o campo
    const buscaRapida = () =>
      comLoading(async () => {
        const termo = (fastFind.value || "").trim();

        vehicleFilter.value = filtroVazio();

        if (!termo) return await aplicaFiltro();

        vehicleFilter.value.modelo = termo;

        const veiculos = await aplicaFiltro();

        // sem resultado por modelo, tenta pelo código fipe
        if (veiculos === null || veiculos.length) return;

        vehicleFilter.value = filtroVazio();
        vehicleFilter.value.fipe = termo;

        await aplicaFiltro();
      });

    const aoFiltrarPeloHeader = (props: VehicleFilterType) => {
      fastFind.value = '';
      vehicleFilter.value = props;

      comLoading(aplicaFiltro);
    };

    // fecha modal de adicionar e recarrega lista quando o modal emitir sucesso
    const aoAdicionarVeiculo = async (payload) => {
      if (!payload || !payload.success) return;

      fechaAdicionarVeiculoModal();

      await comLoading(aplicaFiltro);
    };

    // atualiza os contadores de filtro dos veículos editados, sem recarregar a lista
    const atualizaListaVeiculos = ({ veiculos, produtos }) => {
      const editados = new Set(veiculos.map(({ id }) => id));

      vehiclesArray.value.forEach(veiculo => {
        if (!editados.has(veiculo.id)) return;

        veiculo.filtro_oleo = produtos.filtro_oleo;
        veiculo.filtro_ar = produtos.filtro_ar;
        veiculo.filtro_ac = produtos.filtro_ac;
        veiculo.filtro_combustivel = produtos.filtro_combustivel;

        veiculo.filtros = produtos.filtros;
      });
    };

    emitter.on("buscar-filtro-veiculo", aoFiltrarPeloHeader);
    emitter.on("adicionar-veiculo:finish", aoAdicionarVeiculo);
    emitter.on("atualiza-lista-veiculos", atualizaListaVeiculos);

    // ############ MODAL DE VEICULOS #############

    function mudaEditarMultiplos() {
      editarMultiplos.value = !editarMultiplos.value;
      veiculosSelecionado.value = [];

      if (editarMultiplos.value) return;

      vehiclesArray.value.forEach(e => (e.selecionado = false));
    }

    function selecionaVeiculo(veiculo) {
      veiculo.selecionado = !veiculo.selecionado;

      if (veiculo.selecionado) veiculosSelecionado.value.push(veiculo);
      else veiculosSelecionado.value = veiculosSelecionado.value.filter(e => e !== veiculo);
    }

    function escolheVeiculo(veiculo) {
      veiculosSelecionado.value = [veiculo];
      abreVeiculoModal();
    }

    
    function pegaModal(id: string) {
      const el = document.querySelector(`#${id}`);

      if (!el) return null;

      return Modal.getInstance(el) || new Modal(el);
    }

    function abreVeiculoModal() {
      pegaModal("EditaVeiculo")?.show();
    }

    function fechaVeiculoModal() {
      pegaModal("EditaVeiculo")?.hide();
    }

    function abreAdicionarVeiculoModal() {
      pegaModal("ModalAdicionarVeiculo")?.show();
    }

    function fechaAdicionarVeiculoModal() {
      pegaModal("ModalAdicionarVeiculo")?.hide();
    }

    // ############ MODAL DE VEICULOS #############

    onMounted(() => {
      buscaTotalVeiculos();

      const alvo = document.querySelector("#load");
      if (!alvo) return;

      observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) handlePage(page.value ? page.value + 1 : 1);
      });

      observer.observe(alvo);
    });

    onUnmounted(() => {
      observer?.disconnect();

      emitter.off("buscar-filtro-veiculo", aoFiltrarPeloHeader);
      emitter.off("adicionar-veiculo:finish", aoAdicionarVeiculo);
      emitter.off("atualiza-lista-veiculos", atualizaListaVeiculos);
    });

    return {
      resultados,
      page,
      maxLimitByPage,
      vehiclesArray,
      filtrosVeiculo,
      handlePage,
      vehicleLoad,
      buscandoVeiculos,
      primeiraBuscaFeita,
      vehicleFilter,
      history,
      abreVeiculoModal,
      fechaVeiculoModal,
      abreAdicionarVeiculoModal,
      fechaAdicionarVeiculoModal,
      veiculosSelecionado,
      editarMultiplos,
      selecionaVeiculo,
      escolheVeiculo,
      mudaEditarMultiplos,
      fastFind,
      buscaRapida
    };
  },
});
